import { memo } from 'react';
import styles from '../../styles/Display.module.css';
import useRise from '../../hooks/useRise';
import { risenTop, type DepthPoint, type ProjectedScreen, type ScreenRun } from '../../engine/display/coverScreens';

/**
 * THE TAKE-COVER CUE ON A FIGURE (2026-10-25): curved glass screens on the base ring, one per enemy gun.
 *
 * Drawn twice per figure - `far` before the body, `near` after it - so he stands inside his screens (the
 * engine split them about his depth). Glass in the cover colour, fading toward the ground, a lit rim, the
 * coloured foot kept on the ground; out of sight a hollow dashed rim; open a flat hot arc (drawn with `far`,
 * on the ground under him). They rise once when `riseKey` changes, still with motion off.
 *
 * Never takes the pointer (`pointer-events: none`, aria-hidden): a screen inside a marker that did would steal
 * its drag, its tape and its clicks. The tooltip is the panel's hit test (`screenTipAt`).
 */
interface CoverScreensProps {
  id: string;
  screens: ProjectedScreen[];
  side: 'far' | 'near';
  riseKey: string;
  still: boolean;
}

const pts = (list: readonly DepthPoint[]) => list.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

function RunShape({ run, rise, level }: { run: ScreenRun; rise: number; level: string }) {
  const top = risenTop(run, rise);
  const first = run.foot.length - 1;
  return (
    <>
      <polygon
        className={styles.coverGlass}
        data-level={level}
        points={pts([...run.foot, ...[...top].reverse()])}
        fill={`url(#cover-glass-${level})`}
      />
      <polyline className={styles.coverFoot} data-level={level} points={pts(run.foot)} />
      <polyline className={styles.coverRim} data-level={level} points={pts(top)} />
      {run.startsScreen && <line className={styles.coverPost} data-level={level} {...post(run.foot[0], top[0])} />}
      {run.endsScreen && (
        <line className={styles.coverPost} data-level={level} {...post(run.foot[first], top[first])} />
      )}
    </>
  );
}

const post = (a: DepthPoint, b: DepthPoint) => ({
  x1: a.x.toFixed(1),
  y1: a.y.toFixed(1),
  x2: b.x.toFixed(1),
  y2: b.y.toFixed(1),
});

function CoverScreensImpl({ id, screens, side, riseKey, still }: CoverScreensProps) {
  const rise = useRise(riseKey, still);
  return (
    <g className={styles.coverScreens} data-testid={`cover-screens-${side}-${id}`} aria-hidden="true">
      {screens.map((s) =>
        s.flat ? (
          side === 'far' && (
            <polyline
              key={s.threatId}
              className={styles.coverFlat}
              data-testid={`cover-screen-${id}-${s.threatId}`}
              data-level="none"
              points={pts(s.arc)}
            />
          )
        ) : (
          <g
            key={s.threatId}
            data-testid={side === 'far' ? `cover-screen-${id}-${s.threatId}` : undefined}
            data-cover-threat={s.threatId}
            data-level={s.level}
          >
            {s.runs
              .filter((r) => r.far === (side === 'far'))
              .map((r, i) => (
                <RunShape key={i} run={r} rise={rise} level={s.level} />
              ))}
          </g>
        ),
      )}
    </g>
  );
}

export const CoverScreens = memo(CoverScreensImpl);

/** The glass gradients, once per map: the cover colour, fading toward the ground (objectBoundingBox, bottom up). */
export function CoverGlassDefs() {
  const LEVELS = ['low', 'half', 'threeQuarter', 'full', 'out'];
  return (
    <defs>
      {LEVELS.map((level) => (
        <linearGradient key={level} id={`cover-glass-${level}`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" className={styles.coverGlassFoot} data-level={level} />
          <stop offset="1" className={styles.coverGlassTop} data-level={level} />
        </linearGradient>
      ))}
    </defs>
  );
}
